import { Box, type SxProps, type Theme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { ReadOnlyMetadataField } from "./ReadOnlyMetadataField";

export type SftpCredentials = {
	address: string;
	port: number;
	user: string;
	password: string;
};

/** The command a vendor pastes into a terminal. One definition, so the shown and copied text agree. */
const connectCommand = ({ address, port, user }: SftpCredentials): string => `sftp -P ${port} ${user}@${address}`;

type Props = {
	credentials: SftpCredentials;
	/** Base testid; each field appends its own name (`-address`, `-password`, …). */
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * The connection details for one SFTP login, as read-only copyable fields plus the ready-made command.
 *
 * One component for every surface that hands these to a vendor — the support-only folder dialog, the
 * onboarding-tools provisioner, and the token-gated self-setup page — because three hand-rolled copies
 * is how one of them ends up missing the password mask, or the command, or the copy button.
 *
 * The command is DERIVED from the fields beside it rather than passed in, so it can never disagree with
 * the host, port and user it is made of. The password renders masked (`secret`) with copy still
 * working while hidden: copying a credential does not require reading it off the screen.
 */
export const SftpCredentialFields: FC<Props> = ({ credentials, dataTestId, sx }) => {
	const { t } = useTranslation();

	const fields = useMemo(
		() => [
			{ key: "address", label: t("department.config.integrations.sftp.credentials.address"), value: credentials.address },
			{ key: "port", label: t("department.config.integrations.sftp.credentials.port"), value: String(credentials.port) },
			{ key: "user", label: t("department.config.integrations.sftp.credentials.user"), value: credentials.user },
			{ key: "password", label: t("department.config.integrations.sftp.credentials.password"), value: credentials.password, secret: true },
			{
				key: "command",
				label: t("department.config.integrations.sftp.credentials.command"),
				value: connectCommand(credentials),
			},
		],
		[credentials, t],
	);

	return (
		<Box sx={[{ display: "flex", flexDirection: "column", gap: 2 }, ...(Array.isArray(sx) ? sx : [sx])]}>
			{fields.map(({ key, label, value, secret }) => (
				<ReadOnlyMetadataField key={key} label={label} value={value} copyable secret={secret} dataTestId={dataTestId ? `${dataTestId}-${key}` : undefined} />
			))}
		</Box>
	);
};

/** Every field as `Label: value` lines — what the "copy all" action puts on the clipboard. */
export const useCopyAllSftpCredentials = (): ((credentials: SftpCredentials) => void) => {
	const { t } = useTranslation();
	const { copyToClipboard } = useCopyToClipboard();

	return (credentials: SftpCredentials) =>
		void copyToClipboard(
			[
				`${t("department.config.integrations.sftp.credentials.address")}: ${credentials.address}`,
				`${t("department.config.integrations.sftp.credentials.port")}: ${credentials.port}`,
				`${t("department.config.integrations.sftp.credentials.user")}: ${credentials.user}`,
				`${t("department.config.integrations.sftp.credentials.password")}: ${credentials.password}`,
				`${t("department.config.integrations.sftp.credentials.command")}: ${connectCommand(credentials)}`,
			].join("\n"),
		);
};
